import * as THREE from 'three';

export function createDeathRecoverySystem({ scene, world, player, t, showStatusByKey, recoveryLabelEl }) {
  let deathRecoveryMarker = null;
  let deathRecoveryActive = false;
  let deathRecoverySource = '';
  let deathRecoveryPendingDrops = 0;
  let deathRecoveryBatchSeq = 0;
  let deathRecoveryHudSampleTimer = 0;
  let deathRecoveryMarkerDimension = 'overworld';
  const deathRecoveryPlayerDelta = new THREE.Vector3();

  function createDeathRecoveryMarker() {
    const group = new THREE.Group();
    group.visible = false;
    group.renderOrder = 950;

    const beamGeo = new THREE.CylinderGeometry(0.22, 0.22, 86, 18, 1, true);
    const beamMat = new THREE.MeshBasicMaterial({
      color: 0x8bdfff,
      transparent: true,
      opacity: 0.17,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    const beam = new THREE.Mesh(beamGeo, beamMat);
    beam.position.y = 43;
    group.add(beam);

    const coreGeo = new THREE.CylinderGeometry(0.06, 0.06, 92, 12, 1, true);
    const coreMat = new THREE.MeshBasicMaterial({
      color: 0xc9f4ff,
      transparent: true,
      opacity: 0.42,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    core.position.y = 46;
    group.add(core);

    const ringGeo = new THREE.TorusGeometry(0.9, 0.08, 14, 42);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x8edcff,
      transparent: true,
      opacity: 0.78,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.5;
    group.add(ring);

    const haloGeo = new THREE.TorusGeometry(1.45, 0.09, 14, 44);
    const haloMat = new THREE.MeshBasicMaterial({
      color: 0x57c9ff,
      transparent: true,
      opacity: 0.48,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    halo.rotation.x = Math.PI / 2;
    halo.position.y = 0.44;
    group.add(halo);

    const markerLight = new THREE.PointLight(0x72dcff, 1.08, 22, 2);
    markerLight.position.set(0, 2.2, 0);
    group.add(markerLight);

    scene.add(group);
    return {
      group,
      beam,
      core,
      ring,
      halo,
      markerLight
    };
  }

  function ensureDeathRecoveryMarker() {
    if (deathRecoveryMarker) return deathRecoveryMarker;
    deathRecoveryMarker = createDeathRecoveryMarker();
    return deathRecoveryMarker;
  }

  function clearDeathRecoveryMarker({ recovered = false } = {}) {
    deathRecoveryActive = false;
    deathRecoverySource = '';
    deathRecoveryPendingDrops = 0;
    deathRecoveryMarkerDimension = 'overworld';
    deathRecoveryHudSampleTimer = 0;
    if (recoveryLabelEl) recoveryLabelEl.style.display = 'none';
    if (deathRecoveryMarker?.group) deathRecoveryMarker.group.visible = false;
    if (recovered) {
      showStatusByKey('status.death_recovery_done', {}, 'level', 2.4);
    }
  }

  function activateDeathRecoveryMarker(deathPosition, source, totalDrops = 0) {
    if (!deathPosition || !source || totalDrops <= 0) {
      clearDeathRecoveryMarker();
      return;
    }

    const marker = ensureDeathRecoveryMarker();
    const x = Number(deathPosition.x) || 0;
    const z = Number(deathPosition.z) || 0;
    const dimension = world.getDimensionAt(x, z);
    const surfaceY = Number(world.getSurfaceHeight(x, z)) || 0;
    const baseY = Math.max(surfaceY + 1.2, (Number(deathPosition.y) || 0) - 1.2);

    marker.group.position.set(x, baseY, z);
    marker.group.visible = true;
    marker.ring.rotation.y = 0;
    marker.halo.rotation.y = 0;
    marker.core.scale.setScalar(1);
    marker.beam.scale.setScalar(1);

    deathRecoveryActive = true;
    deathRecoverySource = source;
    deathRecoveryPendingDrops = Math.max(0, Math.floor(totalDrops));
    deathRecoveryMarkerDimension = dimension;
    deathRecoveryHudSampleTimer = 0;

    showStatusByKey(
      'status.death_recovery_hint',
      {
        count: deathRecoveryPendingDrops,
        x: Math.round(x),
        y: Math.round(baseY),
        z: Math.round(z)
      },
      'level',
      3.4
    );
  }

  function onDeathRecoveryDropPicked(source, count = 1) {
    if (!deathRecoveryActive) return;
    if (!source || source !== deathRecoverySource) return;

    deathRecoveryPendingDrops = Math.max(0, deathRecoveryPendingDrops - Math.max(1, Math.floor(Number(count) || 1)));
    if (deathRecoveryPendingDrops <= 0) {
      clearDeathRecoveryMarker({ recovered: true });
    }
  }

  function updateDeathRecoveryMarker(delta, timeSeconds, currentDimension = null) {
    const marker = ensureDeathRecoveryMarker();
    if (!deathRecoveryActive) {
      marker.group.visible = false;
      return;
    }

    const dimension = currentDimension || 'overworld';
    if (dimension !== deathRecoveryMarkerDimension) {
      marker.group.visible = false;
      return;
    }
    marker.group.visible = true;

    const pulse = 0.7 + 0.3 * (0.5 + 0.5 * Math.sin(timeSeconds * 3.5));
    marker.beam.material.opacity = 0.12 + pulse * 0.15;
    marker.core.material.opacity = 0.3 + pulse * 0.28;
    marker.ring.material.opacity = 0.5 + pulse * 0.33;
    marker.halo.material.opacity = 0.28 + pulse * 0.22;
    marker.ring.rotation.y += delta * 1.35;
    marker.halo.rotation.y -= delta * 0.92;
    marker.ring.position.y = 0.48 + Math.sin(timeSeconds * 2.4) * 0.06;
    marker.halo.position.y = 0.42 + Math.sin(timeSeconds * 1.8 + 1.4) * 0.05;
    marker.markerLight.intensity = 0.88 + pulse * 0.42;
  }

  function updateDeathRecoveryHud(delta = 0.016) {
    if (!recoveryLabelEl) return;
    if (!deathRecoveryActive || !deathRecoveryMarker?.group?.visible) {
      recoveryLabelEl.style.display = 'none';
      return;
    }

    deathRecoveryHudSampleTimer = Math.max(0, deathRecoveryHudSampleTimer - Math.max(0, delta));
    if (deathRecoveryHudSampleTimer > 0) return;
    deathRecoveryHudSampleTimer = 0.08;

    const playerPos = player.getPosition();
    deathRecoveryPlayerDelta.copy(deathRecoveryMarker.group.position).sub(playerPos);
    const distance = Math.max(0, Math.round(Math.hypot(deathRecoveryPlayerDelta.x, deathRecoveryPlayerDelta.z)));
    recoveryLabelEl.style.display = 'block';
    recoveryLabelEl.innerText = t('hud.death_recovery_value', {
      x: Math.round(deathRecoveryMarker.group.position.x),
      y: Math.round(deathRecoveryMarker.group.position.y),
      z: Math.round(deathRecoveryMarker.group.position.z),
      distance
    });
  }

  return {
    activateDeathRecoveryMarker,
    clearDeathRecoveryMarker,
    onDeathRecoveryDropPicked,
    updateDeathRecoveryMarker,
    updateDeathRecoveryHud,
    isActive: () => deathRecoveryActive,
    getMarker: () => deathRecoveryMarker,
    nextBatchSeq: () => ++deathRecoveryBatchSeq
  };
}
